import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";

import { CabeceraDelMantenedor } from "@/components/plantillas/CabeceraDelMantenedor";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Skeleton } from "@/components/ui/skeleton";
import { useFormatoDeSalida } from "@/hooks/useFormatosDeSalida";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";

const TARJETA = "bg-card flex flex-col gap-3 rounded-xl border px-5.5 py-5";
const ROTULO = "text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.04em] uppercase";

/**
 * Detalle de un formato de salida (solo lectura): el orden de capítulos a la
 * izquierda y, a la derecha, sus ítems propios, sus rasgos de presentación y
 * las plantillas que lo admiten (con "Por defecto" donde es el que se propone).
 */
export function FormatoDeSalida() {
  const { id: idDeLaRuta } = useParams();
  const id = Number(idDeLaRuta);
  const navigate = useNavigate();

  const consulta = useFormatoDeSalida(id);
  const estado = estadoDeConsulta(consulta);
  const formato = consulta.data;

  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <CabeceraDelMantenedor vista="formatos" />

      <Link
        to="/admin/contenidos/formatos"
        className="text-ink-muted hover:text-ink mb-3.5 inline-flex items-center gap-1.5 text-[0.8125rem] font-bold"
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
        Formatos de salida
      </Link>

      {estado.sinConexion && <EstadoSinConexion que="el formato" />}

      {estado.hayError && (
        <EstadoDeError
          titulo="No se pudo cargar el formato"
          error={consulta.error}
          onReintentar={consulta.refetch}
          reintentando={consulta.isFetching}
        />
      )}

      {estado.cargando && (
        <div className="space-y-3">
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-80 w-full rounded-xl" />
        </div>
      )}

      {formato ? (
        <>
          <h2 className="text-ink text-h2 leading-tight">{formato.nombre}</h2>
          {formato.clientes ? (
            <p className="text-ink-body mt-1.5 text-sm font-medium">{formato.clientes}</p>
          ) : null}

          <div className="mt-5 grid items-start gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,23.75rem),1fr))]">
            <section className={TARJETA}>
              <span className={ROTULO}>Orden de capítulos</span>
              <ol aria-label="Orden de capítulos" className="flex flex-col">
                {formato.orden_capitulos.map((capitulo, i) => (
                  <li key={`${i}-${capitulo}`} className="flex items-stretch gap-3">
                    <div className="flex w-6 flex-none flex-col items-center">
                      <span className="bg-ink flex size-6 flex-none items-center justify-center rounded-full font-mono text-[0.65625rem] font-bold text-white">
                        {i + 1}
                      </span>
                      {i < formato.orden_capitulos.length - 1 ? (
                        <span className="bg-divisor min-h-2.5 w-[1.5px] flex-1" aria-hidden="true" />
                      ) : null}
                    </div>
                    <span className="text-ink-body pt-[0.1875rem] pb-3 text-[0.84375rem] leading-[1.4] font-semibold">
                      {capitulo}
                    </span>
                  </li>
                ))}
              </ol>
            </section>

            <div className="flex flex-col gap-4">
              <section className={TARJETA}>
                <span className={ROTULO}>Ítems propios del formato</span>
                {formato.items_propios.length === 0 ? (
                  <span className="text-ink-hint text-[0.8125rem] font-semibold">No agrega ítems.</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {formato.items_propios.map((item) => (
                      <span
                        key={item}
                        className="bg-info-soft text-info-soft-foreground rounded-lg px-2.5 py-1.5 text-[0.78125rem] font-semibold"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                )}
              </section>

              {formato.rasgos ? (
                <section className={TARJETA}>
                  <span className={ROTULO}>Rasgos de presentación</span>
                  <p className="text-ink-body text-[0.84375rem] leading-[1.6] font-medium text-pretty">
                    {formato.rasgos}
                  </p>
                </section>
              ) : null}

              <section className={TARJETA}>
                <span className={ROTULO}>Plantillas compatibles</span>
                <div className="flex flex-col gap-0.5">
                  {formato.plantillas.map((plantilla) => (
                    <button
                      key={plantilla.id}
                      type="button"
                      onClick={() => navigate(`/admin/contenidos/plantillas/${plantilla.id}`)}
                      className="text-ink-body hover:bg-superficie-sutil flex items-center gap-2 rounded-[0.5625rem] px-2 py-2 text-left text-[0.8125rem] font-semibold"
                    >
                      <span className="flex-1">{plantilla.nombre}</span>
                      {plantilla.por_defecto ? (
                        <span className="bg-success-soft text-success-soft-foreground rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold">
                          Por defecto
                        </span>
                      ) : null}
                      <ChevronRight className="text-ink-ghost size-4" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
